import * as React from 'react';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Form, Modal, ModalBody, ModalHeader } from 'reactstrap';
import { useTranslations } from '../../../../store/translations/hooks';
import { TK } from '../../../../store/translations/translationKeys';
import ButtonsContainer from '../../../../components/ButtonsContainer';
import { TextInputField, ComboField, MultipleTextField, AutocompleteTextField, NumberInputField, PlaceholderTextField } from '../../../../components/Forms/fields';
import { ReportFieldDefinition, ReportFieldFlag } from '../../../../models';
import { ReportFieldType } from '../../../../models';
import { Guid } from '../../../../shared/utils';
import { ImageInputField } from '../../../../components/Forms/fields/ImageInputField';
import MarkerForm from './MarkerForm';
import { TableColumnsField } from './TableColumnsField';
import { reportFieldModalConfig } from './reportFieldModalConfig';
import AppButton from '../../../../components/AppButton';

export interface ReportFieldModalProps {
    id: string,
    defaultValue?: ReportFieldDefinition,
    isOpen: boolean,
    mode: 'edit'|'create',
    forbiddenNames?: string[], 
    onClose: () => void,
    onSave: (value: ReportFieldDefinition) => void,
}

const getAutoLabel = () => `auto-${Guid.newGuid()}` 

const multipleValuesSeparator = '|';

const ReportFieldModal: FC<ReportFieldModalProps> = ({
    id, 
    defaultValue,
    isOpen,
    mode,
    forbiddenNames = [],
    onClose,
    onSave,
}) => {
    const t = useTranslations();

    const [field, setField] = useState<ReportFieldDefinition>({type: ReportFieldType.Text} as ReportFieldDefinition);

    useEffect(() => setField({type: ReportFieldType.Text, ...defaultValue} as ReportFieldDefinition), [defaultValue]);


    const fieldTypesTranslations = useMemo(() => Object.keys(ReportFieldType)
        .filter(v => typeof ReportFieldType[v as any] === "number")
        .map(key => ({
            key: ReportFieldType[key as any] as any as ReportFieldType, 
            translation: t((key.charAt(0).toLocaleLowerCase() + key.slice(1)) as TK)
        }))
    , [t]);

    const fieldFlagsValues = useMemo(() => Object.fromEntries(Object.keys(ReportFieldFlag)
        .filter(v => typeof ReportFieldFlag[v as any] === "number")
        .map(key => key === 'Block' ? [
            t(TK.fullWidth),
            ReportFieldFlag[key as any] as any as ReportFieldFlag, 
        ] : [
            t((key.charAt(0).toLocaleLowerCase() + key.slice(1)) as TK),
            ReportFieldFlag[key as any] as any as ReportFieldFlag, 
        ]))
    , [t]);

    const fieldFlagsTranslations = useMemo(() => Object.fromEntries(
        Object.entries(fieldFlagsValues).map(([key, value]) => [value, key])),
        [fieldFlagsValues])



    const fieldConfig = reportFieldModalConfig[field.type];

    const updateFieldLabel = useCallback((label: string) => setField(prev => ({...prev, label})), [setField]);
    const setMandatory = useCallback((val: boolean) => setField(prev => ({...prev, isMandatory: val})), [setField]);
    const updateFieldDescription = useCallback((description: string) => setField(prev => ({...prev, description})), [setField]);
    const updateFieldHelpNotes = useCallback((helpNotes: string) => setField(prev => ({...prev, helpNotes})), [setField]);
    const updateDefaultValue = useCallback((defaultValue: string) => setField(prev => ({...prev, defaultValue})), [setField]);
    const updateFontSize = useCallback((fontSize: number = 0) => setField(prev => ({...prev, fontSize})), [setField]);
    const updateHeightPx = useCallback((height: number = 0) => setField(prev => ({...prev, height: height/14})), [setField]); // Convert px to rem (1rem = 14px)
    const updateFlags = useCallback((flagsTranslated: string[]) => setField(prev => ({...prev, flags: flagsTranslated.map(f => fieldFlagsValues[f])})), [setField]);
    const updateFieldType = useCallback((typeTranslated: string) => setField(prev => {
        const newType = fieldTypesTranslations.find(f => f.translation === typeTranslated)?.key || fieldTypesTranslations[0].key;
        const fieldConfig = reportFieldModalConfig[newType];
        return {
            ...prev,
            type: newType,
            aditionalFields: undefined,
            height: undefined,
            fontSize: undefined,
            flags: fieldConfig.autoFlags?.length ? fieldConfig.autoFlags : prev.flags,
            label: fieldConfig.autoLabel ? getAutoLabel() : prev.label?.startsWith('auto-') ? '' : prev.label,
        };
    }), [setField, fieldTypesTranslations, fieldConfig]);

    const updateFieldColumns = useCallback((columns: string[], widths: (string|undefined)[]) => setField(prev => ({
        ...prev, 
        aditionalFields: {...prev?.aditionalFields, columns: columns.join(multipleValuesSeparator), widths: widths.map(v => v ? v : '').join(multipleValuesSeparator)},
    })), [setField]);

    const updateFieldOptions = useCallback((options: string[]) => setField(prev => ({
        ...prev, 
        aditionalFields: {...prev?.aditionalFields, options: options.join(multipleValuesSeparator)},
    })), [setField]);

    const updateFieldImage = useCallback((image?: string) => setField(prev => ({
        ...prev, 
        aditionalFields: {...prev?.aditionalFields, image: image as any},
    })), [setField]);

    const addAditionalFieldMarker = useCallback(() => setField(prev => ({
        ...prev, 
        aditionalFields: {...prev?.aditionalFields, markers: `${prev?.aditionalFields?.markers ?? ''}${multipleValuesSeparator}new`},
    })), [setField]);

    const removeAditionalFieldMarker = useCallback(() => setField(prev => {
        const markers = prev?.aditionalFields?.markers ?? '';
        return {
            ...prev, 
            aditionalFields: {...prev?.aditionalFields, markers: markers.substring(0, markers.lastIndexOf(multipleValuesSeparator))},
        };
    }), [setField]);

    const updateAditionalFieldMarker = useCallback((prevMarker: string, newMarker: string) => setField(prev => ({
        ...prev, 
        aditionalFields: {
            ...prev.aditionalFields, 
            markers: Array.from(new Set(prev.aditionalFields?.markers?.split(multipleValuesSeparator).filter(v => v) ?? [newMarker]))
                .map(value => value === prevMarker ? newMarker : value)
                .join(multipleValuesSeparator),
        },
    })), [setField]);

    useEffect(() => {
        setField(prev => ({
            ...prev, 
            flags: fieldConfig.autoFlags?.length ? fieldConfig.autoFlags : prev.flags,
            label: fieldConfig.autoLabel ? getAutoLabel() : prev.label,
        }))
    }, [isOpen]);
    
    const isFieldValid = useMemo(() => 
        (field?.label || fieldConfig.autoLabel) 
        && field?.type !== undefined
        && !forbiddenNames.includes(field.label)
    , [field, fieldConfig, forbiddenNames]); 

    return (

        <Modal size={fieldConfig.largeEdition ? 'lg' : 'md'} isOpen={isOpen}>
            <ModalHeader>{mode === 'edit' ? t(TK.edit) : t(TK.newField)}</ModalHeader>
            <ModalBody>
                <Form style={{display: 'flex', marginTop: 'var(--spacer-S)', flexDirection: 'column', gap: 'var(--spacer-M)'}}>
                    
                    <ComboField
                        id={`${id}-type`}
                        label={t(TK.type)}
                        options={fieldTypesTranslations.map(f => f.translation)}
                        value={fieldTypesTranslations.find(f => f.key === field.type)?.translation}
                        onChange={v => v && updateFieldType(v)}
                    />
                    
                    { fieldConfig.showName &&
                        <TextInputField
                            id={`${id}-title`}
                            label={t(TK.title)}
                            value={field.label}
                            onChange={updateFieldLabel}
                        />
                    }

                    { fieldConfig.showMandatoryCombo && 
                        <ComboField 
                            id={`${id}-mandatory-button`}
                            label={t(TK.mandatory)}
                            options={[
                                t(TK.mandatory),
                                t(TK.optional),
                            ]}
                            value={field.isMandatory ? t(TK.mandatory) : t(TK.optional)}
                            onChange={v => setMandatory(v === t(TK.mandatory))}
                        />
                    }

                    { fieldConfig.showFlags && (
                        <AutocompleteTextField
                            id={`${id}-flags`}
                            label={t(TK.otherAttributes)}
                            multiSelect
                            options={Object.keys(fieldFlagsValues)}
                            values={field.flags?.map(f => fieldFlagsTranslations[f])}
                            onValuesChanged={updateFlags}
                        />
                    )}

                    { fieldConfig.showFontSize && (
                        <NumberInputField
                            id={`${id}-font-size`}
                            label={t(TK.fontSize)}
                            value={field.fontSize ?? 14}
                            onChange={updateFontSize}
                        />
                    )}

                    { fieldConfig.showHeight && (
                        <NumberInputField
                            id={`${id}-height`}
                            label={t(TK.height)}
                            value={field.height ? field.height * 14 : undefined /* Convert rem to px */}
                            onChange={updateHeightPx}
                            append='px'
                        />
                    )}

                    { fieldConfig.showTableColumns && (
                        <div>
                            <Alert color="secondary">
                                {t(TK.reportTableDefinitionNote)}
                            </Alert>
                            <span>{t(TK.columns)}</span>
                            <TableColumnsField
                                id={`${id}-columns`}
                                columns={field.aditionalFields?.columns?.split(multipleValuesSeparator) || []}
                                widths={field.aditionalFields?.widths?.split(multipleValuesSeparator) || []}
                                onChange={updateFieldColumns}
                            />
                        </div>
                    )}
                    { fieldConfig.showOptions && (
                        <MultipleTextField
                            id={`${id}-options`}
                            label={t(TK.options)}
                            helpText={field.type === ReportFieldType.Table ? t(TK.reportTableOptionsNote) : undefined}
                            values={field.aditionalFields?.options?.split(multipleValuesSeparator) || []}
                            onChange={updateFieldOptions}
                        />
                    )}
                    { fieldConfig.showDescription &&
                        <TextInputField
                            id={`${id}-description`}
                            label={t(TK.description)}
                            value={field.description}
                            onChange={updateFieldDescription}
                        />
                    }
                    { fieldConfig.showDefaultValue && (
                        <PlaceholderTextField
                            id={`${id}-default-value`}
                            label={t(TK.defaultValue)}
                            helpText={t(TK.reportDefaultValueNote)}
                            type="textarea"
                            value={field.defaultValue}
                            onChange={updateDefaultValue}
                        />
                    )}
                    { fieldConfig.showHelpNotes &&
                        <TextInputField
                            id={`${id}-help-notes`}
                            label={t(TK.helpNotes)}
                            type="textarea"
                            value={field.helpNotes}
                            onChange={updateFieldHelpNotes}
                        />
                    }
                    { fieldConfig.showImageMarker &&
                        <>
                            <ImageInputField
                                id={`${id}-image`}
                                label={t(TK.image)}
                                value={field.aditionalFields?.image}
                                onChange={updateFieldImage}
                            />
                            <div>
                                <label style={{display: 'block', marginBottom: '0.5rem'}}>{t(TK.markers)}</label>
                                {field.aditionalFields?.markers?.split(multipleValuesSeparator).filter(v => v).map((value, index) => (
                                    <MarkerForm id={`${id}-marker-${index}`} key={index} value={value} onChange={newValue => updateAditionalFieldMarker(value, newValue)} />
                                ))}
                            </div>
                            <ButtonsContainer>
                                <AppButton
                                    id={`${id}-remove-aditional-marker-button`}
                                    disabled={(field.aditionalFields?.markers?.split(multipleValuesSeparator).filter(v => v).length ?? 0) <= 1}
                                    onClick={removeAditionalFieldMarker}
                                >
                                    {t(TK.remove)}
                                </AppButton>
                                <AppButton onClick={addAditionalFieldMarker}>{t(TK.add)}</AppButton>
                            </ButtonsContainer>
                        </>
                    }
                </Form>
                <hr/>
                <ButtonsContainer>
                    <AppButton id={`${id}-save-button`} color="success" onClick={() => onSave(field)} disabled={!isFieldValid}>{mode === 'edit' ? t(TK.save) : t(TK.add)}</AppButton>
                    <AppButton id={`${id}-cancel-button`} onClick={onClose}>{t(TK.cancel)}</AppButton>
                </ButtonsContainer>
            </ModalBody>
        </Modal>
    );
}

export default ReportFieldModal;